import {Alert, FormHelperText, Grid, IconButton, useTheme} from "@mui/material";
import {MuiRhfSelectField} from "../../shared/components/forms/MuiRhfSelectField";
import {Application, LightWeightApplication} from "../applications/Application";
import {User} from "./User";
import React, {useState} from "react";
import {useFieldArray, useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {ApplicationSelectField} from "../applications/ApplicationSelectField";
import {getApplication} from "../applications/ApplicationsApi";
import {useAxios} from "../../shared/technical/rest/AxiosProvider";
import {formatRole, Role} from "./Role";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {palette} from "../../shared/technical/style/theme";
import {OutlinedAddButton} from "../../shared/components/buttons/OutlinedAddButton";
import {useAccount} from "./AccountProvider";
import {ApplicationRole, mayShowRoleInForm} from "./ApplicationRoles";
import {retrieveError} from "../../shared/components/forms/formUtils";
import {useQuery} from "react-query";
import {ApplicationRoleInfoIcon} from "./ApplicationRoleInfoIcon";
import {CenteredCircularProgress} from "../../shared/components/progress/CenteredCircularProgress";
import {InfoIcon} from "../../shared/components/forms/InfoIcon";

export function ApplicationRolesForm(props: {
  disabled?: boolean
}) {
  const {t} = useTranslation();
  const theme = useTheme();
  const {control, formState: {errors}, watch} = useFormContext<User>();
  const {fields, append, remove} = useFieldArray<User, "applicationRoles">({
    control,
    name: "applicationRoles"
  });

  // console.log('applicationRoles', fields);

  const hasFilteredApplicationRolesWatch = watch('hasFilteredApplicationRoles');

  return (
    <Grid container={true} sx={{marginTop: theme.spacing(5), marginBottom: theme.spacing(4),}}>
      <Grid item={true} lg={12} sx={{
        display: 'flex',
        alignItems: 'center',
        '& > *': {
          marginRight: theme.spacing(2)
        }
      }}>
        <Typography variant={'h4'}>{t('Application.titleLong') + ':'}</Typography>
        {!props.disabled &&
          <OutlinedAddButton onClick={() => append({} as ApplicationRole)} title={t('Application.buttonCreate')} />
        }
        <InfoIcon text={t('ApplicationRoles.infoText')} color={"black"} sx={{marginLeft: 2}} />
      </Grid>
      {retrieveError('applicationRoles', errors) &&
        <FormHelperText error={true}>{retrieveError('applicationRoles', errors)}</FormHelperText>
      }
      {fields
        .map((applicationRole: ApplicationRole, index: number) =>
          <ApplicationRoleForm applicationRole={applicationRole} index={index} remove={remove} disabled={props.disabled} key={index} />
        )}
      {hasFilteredApplicationRolesWatch &&
        <Alert color={"info"}>{t('User.hasFilteredApplicationRoles.message')}</Alert>
      }
    </Grid>
  )
}

export function ApplicationRoleForm(props: {
  applicationRole: ApplicationRole,
  index: number,
  remove: (index?: number | number[]) => void,
  disabled?: boolean
}) {
  const {t} = useTranslation();
  const theme = useTheme();
  const axios = useAxios();
  const account = useAccount();
  const {watch} = useFormContext<User>();

  const [roles, setRoles] = useState<Role[]>([]);

  const applicationWatch: LightWeightApplication | undefined = watch(`applicationRoles.${props.index}.application`);
  const roleWatch: string | undefined = watch(`applicationRoles.${props.index}.role`);
  const role = roles.find(role => role.name === roleWatch);

  // console.info("ApplicationRoleForm", props.applicationRole, applicationWatch, roleWatch, role);

  const {data: application, isLoading} = useQuery(
    ["application", applicationWatch?.id],
    () => getApplication(axios, applicationWatch.id!!),
    {
      enabled: !!applicationWatch?.id,
      onSuccess: (application: Application) => {
        setRoles(application.roles.filter(role => mayShowRoleInForm(account, application, role, props.applicationRole))
        )
      },
      keepPreviousData: true,
    }
  );

  if (isLoading) {
    return <CenteredCircularProgress />
  }

  return (
    <Grid container={true} spacing={3} sx={{marginTop: theme.spacing(3),}} key={props.applicationRole.id}>
      <Grid item={true} xs={5}>
        <ApplicationSelectField name={`applicationRoles.${props.index}.application`} disabled={props.disabled} />
      </Grid>
      <Grid item={true} xs={5}>
        <MuiRhfSelectField
          name={`applicationRoles.${props.index}.role`}
          label={t('User.applicationRoles.role')}
          list={roles.map(role => role.name)}
          getListLabel={(role) => application ? formatRole(application, role) : ''}
          getListKey={(role) => role}
          disabled={props.disabled}
        />
      </Grid>
      <Grid item={true} xs={1} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {role && application && <ApplicationRoleInfoIcon application={application} role={role} />}
      </Grid>
      {!props.disabled &&
        <Grid item={true} xs={1} sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
          <IconButton
            onClick={() => props.remove(props.index)}
            sx={{color: palette.primary,}}
            size="large">
            <DeleteOutlineIcon />
          </IconButton>
        </Grid>
      }
    </Grid>
  );
}
